import { softphoneApi, type HeartbeatPayload } from "@/shared/api/softphoneApi";
import { SOFTPHONE_HEARTBEAT_MS } from "./softphoneConfig";
import { derivePhoneStatus, useSoftphoneStore } from "./softphoneStore";

/**
 * Heartbeat do agente (`POST /rest/status/heartbeat`, multipart) — alimenta `phonemanager_phone`
 * e `phonemanager_phone_activity` (todos os relatórios). Regras, iguais ao softphone legado:
 *
 *  - envia imediatamente a cada mudança de (sessionStatus, sessionInfo, phoneStatus, phoneInfo,
 *    callid, sysid, softphonecallid), em ordem (fila serial);
 *  - reenvia o último estado periodicamente (padrão 30s) enquanto sessão ou aparelho não estão
 *    OFFLINE — o janitor (`LogService.logMaintenance`) derruba para "Connection lost" quem fica
 *    60s sem `statusUpdate`;
 *  - callid/sysid/softphonecallid só existem com o aparelho em RINGING/DIALING/INUSE.
 *    `phoneInfo` = "Nome <sip:user@host>" do outro lado (o backend registra uma atividade nova
 *    quando phoneStatus OU phoneInfo mudam).
 */

export function currentHeartbeat(): HeartbeatPayload {
  const s = useSoftphoneStore.getState();
  const phoneStatus = derivePhoneStatus(s);
  const inCall = phoneStatus === "RINGING" || phoneStatus === "DIALING" || phoneStatus === "INUSE";
  const call = s.call;
  return {
    sessionStatus: s.sessionStatus,
    sessionInfo: s.sessionInfo,
    phoneStatus,
    phoneInfo: inCall && call ? call.remote.phoneInfo : null,
    callid: inCall && call ? call.remote.callid : null,
    sysid: inCall && call ? call.remote.sysid : null,
    softphonecallid: inCall && call ? (call.softphoneCallId ?? call.sipCallId) : null,
  };
}

function keyOf(p: HeartbeatPayload) {
  return [p.sessionStatus, p.sessionInfo, p.phoneStatus, p.phoneInfo, p.callid, p.sysid, p.softphonecallid].join("|");
}

function isIdle(p: HeartbeatPayload) {
  return p.sessionStatus === "OFFLINE" && p.phoneStatus === "OFFLINE";
}

type HeartbeatOptions = {
  /** 401 sem refresh possível (token revogado) — o runtime faz o logout. */
  onUnauthorized: () => void;
};

let running = false;
let lastKey: string | null = null;
let lastSentAt = 0;
let timer: number | null = null;
let unsub: (() => void) | null = null;
let chain: Promise<void> = Promise.resolve();
let opts: HeartbeatOptions | null = null;

function send(p: HeartbeatPayload) {
  lastSentAt = Date.now();
  chain = chain
    .then(() => softphoneApi.heartbeat(p))
    .catch((e: unknown) => {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 401) opts?.onUnauthorized();
      // Outros erros: o próximo ciclo reenvia.
    });
}

function tick() {
  if (!running) return;
  const p = currentHeartbeat();
  const k = keyOf(p);
  if (k !== lastKey) {
    lastKey = k;
    send(p);
    return;
  }
  if (!isIdle(p) && Date.now() - lastSentAt >= SOFTPHONE_HEARTBEAT_MS - 500) send(p);
}

export function startHeartbeat(options: HeartbeatOptions) {
  if (running) return;
  running = true;
  opts = options;
  lastKey = null;
  tick();
  unsub = useSoftphoneStore.subscribe(() => tick());
  timer = window.setInterval(tick, 5000);
}

/** Para o loop. `final` é enviado uma última vez (ex.: OFFLINE no logout). */
export async function stopHeartbeat(final?: HeartbeatPayload): Promise<void> {
  if (!running) return;
  running = false;
  unsub?.();
  unsub = null;
  if (timer != null) {
    window.clearInterval(timer);
    timer = null;
  }
  if (final && keyOf(final) !== lastKey) {
    lastKey = keyOf(final);
    send(final);
  }
  await chain.catch(() => {});
}

/** Fechamento da aba: para o loop e, se `final`, envia com `fetch keepalive` (sobrevive ao unload). */
export function heartbeatOnUnload(final: HeartbeatPayload | null) {
  if (!running) return;
  running = false;
  unsub?.();
  unsub = null;
  if (timer != null) {
    window.clearInterval(timer);
    timer = null;
  }
  if (final) softphoneApi.heartbeatKeepalive(final);
}

export function isHeartbeatRunning() {
  return running;
}
